/**
 * DiscoveryAgentsPageV2 — Phase 5 of the Rebirth migration.
 *
 * Discovery → Agents (formerly /shadow-discovery). Per Design Principles
 * §3.3.1, this page lists agents the platform has *discovered* but that
 * are not yet under governance. The primary action per row is "Promote
 * to ExteLet" — which deep-links into the GEL registration flow at
 * /gel-agents.
 *
 * Backend: tries api.listShadowAgents() (or whatever the v1 page uses);
 * falls back to representative demo data so the page is renderable in
 * dev / brand-new tenants.
 */
import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Compass, Search as SearchIcon, RefreshCw, ArrowRight, Globe } from 'lucide-react';
import * as api from '../services/api';
import { useToast } from '../contexts/ToastContext';
import { isRebirthDiscoveryEnabled } from '../utils/featureFlags';
import ShadowDiscoveryPage from './ShadowDiscoveryPage';
import {
  Button, Pill, Table, Slideout, EmptyState, Card, Skeleton, MenuDropdown,
} from '../components/v2';

const DEMO = [
  { id: 'd1', name: 'partner-llm-7',          endpoint: 'https://partner.example.com/llm/v1', confidence: 0.94, status: 'unregistered', firstSeenAt: '2026-05-08T11:02:00Z', lastSeenAt: '2026-05-08T13:55:00Z', source: 'tenant-egress-monitor', evidence: '4,128 outbound calls in last 24h' },
  { id: 'd2', name: 'pulse-classifier-3',     endpoint: 'https://pulse.io/api/classify',     confidence: 0.81, status: 'unregistered', firstSeenAt: '2026-05-07T15:42:00Z', lastSeenAt: '2026-05-08T13:48:00Z', source: 'connector-anomaly-detector', evidence: 'unrecognized model signature' },
  { id: 'd3', name: 'aria-prod-replica',      endpoint: 'https://aria-mirror.svc/exec',      confidence: 0.72, status: 'pending_review', firstSeenAt: '2026-05-08T09:14:00Z', lastSeenAt: '2026-05-08T13:30:00Z', source: 'gateway-shadow-traffic', evidence: 'mirrored traffic of registered agent (Aria Threat)' },
  { id: 'd4', name: 'unknown-llm-eu-west-1',  endpoint: 'https://eu1.partner.dev/v3/chat',   confidence: 0.66, status: 'unregistered', firstSeenAt: '2026-05-06T17:21:00Z', lastSeenAt: '2026-05-08T12:08:00Z', source: 'dpg-classification', evidence: 'PII flow with no policy bound' },
];

function DiscoveryAgentsPageV2Inner() {
  const navigate = useNavigate();
  const { addToast } = useToast();
  const [items, setItems] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [selected, setSelected] = useState(null);
  const [search, setSearch] = useState('');

  const load = useCallback(async () => {
    setRefreshing(true);
    try {
      // Bracket-notation guards CRA's webpack static-resolution against
      // soft-dependencies that may not yet exist on the api module —
      // see W134 / AgentDetailPageV2 for the same pattern.
      let res;
      const fetchList = api['listShadowAgents'];
      const fetchFallback = api['getShadowDiscovery'];
      if (typeof fetchList === 'function')          res = await fetchList();
      else if (typeof fetchFallback === 'function') res = await fetchFallback();
      else res = null;

      /* G157 — robust unwrap. The discovery backend returns a Spring Page
       * `{ content: [], totalElements, pageable }`, wrapped by apiFetch as
       * `{ ok, data: <Page> }`. The previous one-liner returned the Page
       * object (not an array) for any shape it didn't recognize, which then
       * crashed `.filter(...)` on line 64 with "items.filter is not a function".
       * Handle the realistic shapes explicitly:
       *   bare array            → use as-is
       *   { ok, data }          → unwrap `.data`, then re-apply rules:
       *     - array             → use
       *     - { content: [] }   → Spring Page  → use .content
       *     - { items:   [] }   → ad-hoc       → use .items
       *     - { agents:  [] }   → ad-hoc       → use .agents
       *   anything unrecognized → fallback to DEMO so the page still renders */
      const unwrap = (r) => {
        if (Array.isArray(r)) return r;
        const d = r?.data ?? r;
        if (Array.isArray(d)) return d;
        if (Array.isArray(d?.content)) return d.content;
        if (Array.isArray(d?.items))   return d.items;
        if (Array.isArray(d?.agents))  return d.agents;
        return null;
      };
      const list = unwrap(res);
      setItems(list && list.length > 0 ? list : DEMO);
    } catch (err) {
      addToast({ tone: 'danger', title: 'Failed to load discoveries', description: err?.message || String(err) });
      setItems(DEMO);
    } finally {
      setRefreshing(false);
    }
  }, [addToast]);

  useEffect(() => { load(); }, [load]);

  const filtered = (items || []).filter((row) =>
    !search || (
      (row.name || '').toLowerCase().includes(search.toLowerCase()) ||
      (row.endpoint || '').toLowerCase().includes(search.toLowerCase())
    )
  );

  const promoteOne = (row) => {
    addToast({ tone: 'info', title: 'Promote to ExteLet', description: `${row.name} — opening GEL registration` });
    navigate('/gel-agents', { state: { discoveredAgent: row } });
  };

  const confidenceTone = (c) => c >= 0.9 ? 'success' : c >= 0.75 ? 'warning' : 'danger';

  /* DEF-DISC-MARGIN-002 — Canon is 1600px (WorkflowDiscoveryPageV2).
   * This inner JSX is currently unreachable: the default export at line
   * 235 delegates to ShadowDiscoveryPageV2. Updated for hygiene so a
   * future refactor that re-enables this path inherits the right canon. */
  return (
    <div className="min-h-full bg-v2-page text-v2-ink p-4 sm:p-7 max-w-[1600px] mx-auto">
      <header className="flex items-start gap-4 mb-5">
        <div>
          <h1 className="text-[22px] font-bold tracking-tight m-0">Discovery · Agents</h1>
          <p className="text-[13px] text-v2-ink-secondary mt-0.5">
            {items == null ? 'Loading…' : `${filtered.length} discovered agent(s) · not yet governed`}
          </p>
        </div>
        <div className="ml-auto flex items-center gap-2">
          <div className="bg-v2-muted border border-transparent rounded-v2-sm h-8 px-3 flex items-center gap-2 hover:border-v2-border hover:bg-v2-surface transition-colors duration-v2">
            <SearchIcon size={14} aria-hidden="true" className="text-v2-ink-muted" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by name or endpoint…"
              className="bg-transparent border-0 outline-none text-[12.5px] text-v2-ink placeholder:text-v2-ink-muted w-[220px]"
            />
          </div>
          <Button variant="ghost" iconLeft={<RefreshCw />} onClick={load} loading={refreshing}>Refresh</Button>
          <MenuDropdown
            trigger={<Button variant="primary">Bulk register</Button>}
            items={[
              { id: 'hdr', header: 'Promote selected discoveries' },
              { id: 'all',  label: 'Register all unregistered',     description: `${filtered.filter(r => r.status === 'unregistered').length} discoveries`, iconTone: 'cyan',   icon: <Globe size={14} />, onSelect: () => navigate('/gel-agents') },
              { id: 'high', label: 'Register high-confidence only', description: 'Confidence ≥ 0.90',                                                          iconTone: 'success', icon: <Globe size={14} />, onSelect: () => navigate('/gel-agents') },
            ]}
          />
        </div>
      </header>

      {items == null && (
        <Card><Card.Body><Skeleton variant="rect" height={240} /></Card.Body></Card>
      )}

      {items != null && filtered.length === 0 && (
        <EmptyState
          icon={<Compass size={20} />}
          title={search ? 'No discoveries match the search' : 'No discovered agents'}
          description={search ? 'Try a shorter query or clear the filter.' : 'The platform will surface unregistered agents here as soon as the egress / DPG monitors detect them.'}
          primaryAction={search ? <Button variant="primary" onClick={() => setSearch('')}>Clear search</Button> : null}
        />
      )}

      {items != null && filtered.length > 0 && (
        <Table
          ariaLabel="Discovered agents"
          columns={[
            { key: 'name', header: 'Discovery', render: (r) => (
              <div>
                <div className="font-semibold text-v2-ink">{r.name}</div>
                <div className="text-[10.5px] text-v2-ink-muted font-mono-v2">{r.endpoint}</div>
              </div>
            )},
            { key: 'confidence', header: 'Confidence', render: (r) => (
              <div className="flex items-center gap-2">
                <Pill tone={confidenceTone(r.confidence)} size="sm">{(r.confidence * 100).toFixed(0)}%</Pill>
              </div>
            )},
            { key: 'source', header: 'Source', render: (r) => (
              <span className="font-mono-v2 text-[11px] text-v2-ink-secondary">{r.source}</span>
            )},
            { key: 'lastSeen', header: 'Last seen', render: (r) => (
              <span className="text-[12px] text-v2-ink-secondary">{r.lastSeenAt ? new Date(r.lastSeenAt).toLocaleString() : '—'}</span>
            )},
            { key: 'status', header: 'Status', render: (r) => (
              <Pill tone={r.status === 'unregistered' ? 'warning' : r.status === 'pending_review' ? 'cyan' : 'muted'} dot size="sm">
                {r.status}
              </Pill>
            )},
            { key: 'actions', header: '', align: 'right', render: (r) => (
              <Button variant="primary" size="sm" iconRight={<ArrowRight />} onClick={(e) => { e.stopPropagation(); promoteOne(r); }}>
                Promote
              </Button>
            )},
          ]}
          rows={filtered}
          rowKey={(r) => r.id}
          onRowClick={setSelected}
        />
      )}

      <Slideout
        isOpen={!!selected}
        onClose={() => setSelected(null)}
        ariaLabel={selected ? `${selected.name} discovery detail` : 'Discovery detail'}
        width={520}
        header={selected && (
          <div>
            <div className="flex items-center gap-2 mb-3">
              <Pill tone={selected.status === 'unregistered' ? 'warning' : 'cyan'} dot size="sm">{selected.status}</Pill>
              <Pill tone={confidenceTone(selected.confidence)} size="sm">{(selected.confidence * 100).toFixed(0)}% confidence</Pill>
            </div>
            <h2 className="text-[18px] font-bold tracking-tight m-0 mb-1">{selected.name}</h2>
            <p className="text-[12px] font-mono-v2 text-v2-ink-secondary truncate">{selected.endpoint}</p>
            <div className="flex items-center gap-2 mt-3">
              <Button variant="primary" size="sm" iconLeft={<Globe />} onClick={() => promoteOne(selected)}>Promote to ExteLet</Button>
              <Button variant="secondary" size="sm">Mark as known</Button>
            </div>
          </div>
        )}
      >
        {selected && (
          <div className="space-y-3 text-[12.5px] text-v2-ink-secondary leading-relaxed">
            <Card padding="sm">
              <Card.Header title="Detection metadata" />
              <Card.Body>
                <dl className="grid grid-cols-[120px_1fr] gap-y-1.5 gap-x-3 text-[12px]">
                  <dt className="text-v2-ink-muted uppercase tracking-wider text-[10.5px] font-semibold pt-0.5">Source</dt>
                  <dd className="text-v2-ink font-mono-v2">{selected.source}</dd>
                  <dt className="text-v2-ink-muted uppercase tracking-wider text-[10.5px] font-semibold pt-0.5">First seen</dt>
                  <dd className="text-v2-ink">{selected.firstSeenAt ? new Date(selected.firstSeenAt).toLocaleString() : '—'}</dd>
                  <dt className="text-v2-ink-muted uppercase tracking-wider text-[10.5px] font-semibold pt-0.5">Last seen</dt>
                  <dd className="text-v2-ink">{selected.lastSeenAt ? new Date(selected.lastSeenAt).toLocaleString() : '—'}</dd>
                  <dt className="text-v2-ink-muted uppercase tracking-wider text-[10.5px] font-semibold pt-0.5">Evidence</dt>
                  <dd className="text-v2-ink">{selected.evidence || '—'}</dd>
                </dl>
              </Card.Body>
            </Card>
            <p>
              Promoting this discovery opens the <strong>GEL Registration wizard</strong> with the endpoint
              + evidence pre-populated. After registration the agent enters the standard B-D-A lifecycle.
            </p>
          </div>
        )}
      </Slideout>
    </div>
  );
}

/* G109 — proper v2 inventory pattern (FacetRail + Table + Slideout).
 * v1 ShadowDiscoveryPage stays as fallback via /shadow-discovery?legacy=1. */
import ResolvedShadowDiscoveryPageV2 from './ShadowDiscoveryPageV2';
export default function ResolvedDiscoveryAgentsPage(props) {
  return <ResolvedShadowDiscoveryPageV2 {...props} />;
}
export { DiscoveryAgentsPageV2Inner };
